/**
 * Доска объявлений в админке.
 *
 * В legacy раздел был заглушкой «Раздел в разработке» — копировать нечего, написан заново.
 * Вёрстка легаси-классами админки (Bootstrap 3 + admin.css), своих классов не вводим.
 *
 * Админ видит ВСЕ объявления, включая снятые, правит любое и снимает с доски.
 * Снятое админом житель обратно вернуть не может (бекенд метит hidden_by='admin') —
 * вернуть может только админ отсюда.
 *
 * API: GET /api/admin/board, GET/PUT /api/admin/board/{id},
 *      POST /api/admin/board/{id}/publish|unpublish, DELETE /api/admin/board/{id}/photos/{photo}.
 */

import { useState, type FormEvent } from 'react'

import { api, ApiError } from '@/api/client'
import type { BoardAd } from '@/api/types'
import { Modal } from '@/components/Modal'
import { useApi } from '@/lib/useApi'
import { useAuthImage } from '@/lib/useAuthImage'
import { priceLabel, PhotoThumb, MAX_PHOTOS, LocalThumb } from '@/pages/BulletinBoardPage'

type StateFilter = '' | 'published' | 'hidden'
type KindFilter = '' | 'service' | 'goods'

export function AdminBulletinBoardPage() {
  const [state, setState] = useState<StateFilter>('')
  const [kind, setKind] = useState<KindFilter>('')

  const query = new URLSearchParams()
  if (state !== '') query.set('state', state)
  if (kind !== '') query.set('kind', kind)
  const suffix = query.toString() === '' ? '' : `?${query.toString()}`

  const { data, loading, reload } = useApi<BoardAd[]>(`/api/admin/board${suffix}`)

  // number — правим существующее, 'new' — правление подаёт своё объявление
  const [editing, setEditing] = useState<number | 'new' | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const ads = data ?? []

  const act = async (id: number, action: 'publish' | 'unpublish'): Promise<void> => {
    setError(null)
    setMessage(null)
    try {
      const result = await api.post<{ message: string }>(`/api/admin/board/${id}/${action}`)
      setMessage(result.message)
      reload()
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Не удалось выполнить действие')
    }
  }

  /** Удалить любое объявление (мягко на бэке): исчезает и из админки, и у жителей. */
  const remove = async (id: number): Promise<void> => {
    if (!window.confirm('Удалить объявление? Оно исчезнет отовсюду — и из админки, и у жителей.')) {
      return
    }
    setError(null)
    setMessage(null)
    try {
      const result = await api.delete<{ message: string }>(`/api/admin/board/${id}`)
      setMessage(result.message)
      reload()
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Не удалось удалить объявление')
    }
  }

  /** Поднять или опустить объявление в ленте: сервер меняет его местами с соседом. */
  const move = async (id: number, direction: 'up' | 'down'): Promise<void> => {
    setError(null)
    setMessage(null)
    try {
      await api.post(`/api/admin/board/${id}/move`, { direction })
      reload()
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Не удалось изменить порядок')
    }
  }

  return (
    <>
      <div className="infobox clearfix">
        {error !== null && (
          <p className="okke" style={{ color: '#ce4844', float: 'left' }}>
            {error}
          </p>
        )}
        {message !== null && (
          <p className="okke" style={{ float: 'left' }}>
            {message}
          </p>
        )}
      </div>

      <div style={{ marginBottom: 12 }}>
        <select
          className="form-control"
          style={{ width: 200, display: 'inline-block', marginRight: 8 }}
          value={state}
          onChange={(event) => setState(event.target.value as StateFilter)}
        >
          <option value="">Все объявления</option>
          <option value="published">На доске</option>
          <option value="hidden">Снятые с публикации</option>
        </select>

        <select
          className="form-control"
          style={{ width: 160, display: 'inline-block' }}
          value={kind}
          onChange={(event) => setKind(event.target.value as KindFilter)}
        >
          <option value="">Услуги и товары</option>
          <option value="service">Только услуги</option>
          <option value="goods">Только товары</option>
        </select>

        {/* Объявление правления: к участку не привязывается */}
        <button
          type="button"
          className="btn btn-success"
          style={{ float: 'right' }}
          onClick={() => setEditing('new')}
        >
          Подать объявление
        </button>
      </div>

      {loading && ads.length === 0 && <p className="text-muted">Загрузка…</p>}
      {!loading && ads.length === 0 && <p className="text-muted">Объявлений нет.</p>}

      {ads.length > 0 && (
        <table className="table table-striped">
          <thead>
            <tr>
              <th style={{ width: 90 }}>Фото</th>
              <th>Название</th>
              <th style={{ width: 90 }}>Тип</th>
              <th style={{ width: 130 }}>Стоимость</th>
              <th style={{ width: 110 }}>Автор</th>
              <th style={{ width: 170 }}>Состояние</th>
              <th style={{ width: 70 }}>Порядок</th>
              <th style={{ width: 210 }}>Управление</th>
            </tr>
          </thead>
          <tbody>
            {ads.map((ad, index) => (
              <tr key={ad.id}>
                <td>
                  <Cover coverId={ad.cover_id} count={ad.photo_count} />
                </td>
                <td>
                  <b>{ad.title}</b>
                  {ad.description !== '' && (
                    <>
                      <br />
                      <span className="text-muted">
                        {ad.description.length > 90
                          ? `${ad.description.slice(0, 90)}…`
                          : ad.description}
                      </span>
                    </>
                  )}
                </td>
                <td>{ad.kind_label}</td>
                <td>{priceLabel(ad.price)}</td>
                <td>
                  {ad.by_admin ? (
                    <span className="label label-primary">Правление</span>
                  ) : (
                    ad.author
                  )}
                </td>
                <td>
                  {ad.is_published ? (
                    <span className="label label-success">на доске</span>
                  ) : (
                    <span className="label label-warning">
                      {ad.hidden_by === 'admin' ? 'снято админом' : 'снято автором'}
                    </span>
                  )}
                </td>
                <td>
                  {/* Порядок ленты: вверх — выше на доске. Двигаем перестановкой соседей. */}
                  <button
                    type="button"
                    className="btn btn-default btn-xs"
                    title="Поднять выше"
                    disabled={index === 0}
                    onClick={() => void move(ad.id, 'up')}
                  >
                    ↑
                  </button>{' '}
                  <button
                    type="button"
                    className="btn btn-default btn-xs"
                    title="Опустить ниже"
                    disabled={index === ads.length - 1}
                    onClick={() => void move(ad.id, 'down')}
                  >
                    ↓
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={() => setEditing(ad.id)}
                  >
                    Изменить
                  </button>{' '}
                  {ad.is_published ? (
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => void act(ad.id, 'unpublish')}
                    >
                      Снять
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={() => void act(ad.id, 'publish')}
                    >
                      Вернуть
                    </button>
                  )}{' '}
                  {/* Удаление — совсем убрать объявление (в отличие от «Снять»). */}
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => void remove(ad.id)}
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing !== null && (
        <AdminAdModal
          adId={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            reload()
          }}
        />
      )}
    </>
  )
}

/** Обложка в таблице: фото закрыто авторизацией, поэтому грузим его через blob. */
function Cover({ coverId, count }: { coverId: number | null; count: number }) {
  const url = useAuthImage(coverId === null ? null : `/board/photo/${coverId}`)

  if (coverId === null) return <span className="text-muted">—</span>

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <img src={url ?? undefined} alt="" style={{ width: 70, height: 70, objectFit: 'cover' }} />
      {count > 1 && (
        <span className="label label-default" style={{ position: 'absolute', right: 2, bottom: 2 }}>
          {count}
        </span>
      )}
    </span>
  )
}

/**
 * Окно объявления в админке: правка любого — и подача СВОЕГО (от правления).
 *
 * adId = null — правление подаёт объявление: оно не привязано к участку (area_fk = NULL),
 * в ленте подписано «Правление», и житель его не правит и не снимает.
 *
 * Фото нового объявления ждут в браузере и уходят сразу после его создания: они
 * привязываются к id, которого до сохранения ещё нет.
 */
function AdminAdModal({
  adId,
  onClose,
  onSaved,
}: {
  adId: number | null
  onClose: () => void
  onSaved: () => void
}) {
  const { data, reload } = useApi<BoardAd>(adId === null ? null : `/api/admin/board/${adId}`)

  const [kind, setKind] = useState<'service' | 'goods'>('goods')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [price, setPrice] = useState('')
  const [contact, setContact] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [filled, setFilled] = useState(adId === null)
  const [pending, setPending] = useState<File[]>([])

  if (data !== null && !filled) {
    setKind(data.kind === 'service' ? 'service' : 'goods')
    setTitle(data.title)
    setDescription(data.description)
    setPrice(data.price ?? '')
    setContact(data.contact)
    setFilled(true)
  }

  const photos = data?.photos ?? []
  const photoCount = adId === null ? pending.length : photos.length

  const sendPhotos = async (id: number, files: File[]): Promise<string | null> => {
    if (files.length === 0) return null
    const form = new FormData()
    for (const file of files) form.append('photo[]', file)

    const result = await api.upload<{ saved: number; errors: string[] }>(
      `/api/admin/board/${id}/photos`,
      form,
    )
    return result.errors.length > 0 ? result.errors.join(' ') : null
  }

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const body = { kind, title, description, price, contact }

      if (adId === null) {
        const created = await api.post<{ id: number }>('/api/admin/board', body)
        const photoError = await sendPhotos(created.id, pending)
        if (photoError !== null) {
          setError(`Объявление опубликовано, но с фото беда: ${photoError}`)
          setBusy(false)
          return
        }
      } else {
        await api.put(`/api/admin/board/${adId}`, body)
      }
      onSaved()
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Не удалось сохранить объявление')
    } finally {
      setBusy(false)
    }
  }

  const pick = async (files: FileList | null): Promise<void> => {
    if (files === null || files.length === 0) return
    setError(null)

    const chosen = Array.from(files).slice(0, Math.max(0, MAX_PHOTOS - photoCount))
    if (chosen.length === 0) {
      setError(`Можно не больше ${MAX_PHOTOS} фото.`)
      return
    }

    if (adId === null) {
      setPending((list) => [...list, ...chosen])
      return
    }
    try {
      const photoError = await sendPhotos(adId, chosen)
      if (photoError !== null) setError(photoError)
      reload()
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Не удалось загрузить фото')
    }
  }

  const removePhoto = async (photoId: number): Promise<void> => {
    if (adId === null) return
    try {
      await api.delete(`/api/admin/board/${adId}/photos/${photoId}`)
      reload()
    } catch {
      setError('Не удалось удалить фото')
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={adId === null ? 'Объявление от правления' : `Объявление №${adId}`}
    >
      <form onSubmit={(event) => void submit(event)}>
        {error !== null && <div className="alert alert-danger">{error}</div>}

        {adId === null && (
          <p className="text-muted">
            Объявление правления: к участку не привязывается, на доске подписано «Правление».
          </p>
        )}
        {data !== null && (
          <p className="text-muted">
            Автор — {data.author}. Правка админом автора не уведомляет.
          </p>
        )}

        <div className="form-group">
          <label>Тип</label>
          <select
            className="form-control"
            value={kind}
            onChange={(event) => setKind(event.target.value === 'service' ? 'service' : 'goods')}
          >
            <option value="goods">Товар</option>
            <option value="service">Услуга</option>
          </select>
        </div>

        <div className="form-group">
          <label>Название</label>
          <input
            type="text"
            className="form-control"
            value={title}
            maxLength={255}
            required
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Описание</label>
          <textarea
            className="form-control"
            rows={4}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Стоимость</label>
          <input
            type="text"
            className="form-control"
            value={price}
            placeholder="Свободный текст. Пусто — «Договорная»"
            onChange={(event) => setPrice(event.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Контакт</label>
          <input
            type="text"
            className="form-control"
            value={contact}
            maxLength={255}
            onChange={(event) => setContact(event.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Фото (не больше {MAX_PHOTOS})</label>
          <div style={{ marginBottom: 8 }}>
            {photos.map((photoId) => (
              <PhotoThumb key={photoId} photoId={photoId} onRemove={() => void removePhoto(photoId)} />
            ))}
            {pending.map((file, index) => (
              <LocalThumb
                key={`${file.name}-${file.size}-${index}`}
                file={file}
                onRemove={() => setPending((list) => list.filter((_, i) => i !== index))}
              />
            ))}
            {photoCount === 0 && (
              <p className="text-muted">
                <small>Фото нет.</small>
              </p>
            )}
          </div>

          <input
            type="file"
            accept="image/*"
            multiple
            disabled={photoCount >= MAX_PHOTOS}
            onChange={(event) => {
              void pick(event.target.files)
              event.target.value = ''
            }}
          />
          <p className="text-muted">
            <small>
              Фото ужимаются на сервере до 700 точек по длинной стороне.
              {adId !== null && ' Неуместное фото жителя админ может удалить крестиком.'}
            </small>
          </p>
        </div>

        <button type="submit" className="btn btn-success" disabled={busy}>
          {busy ? 'Сохраняем…' : 'Сохранить'}
        </button>{' '}
        <button type="button" className="btn btn-default" onClick={onClose}>
          Отмена
        </button>
      </form>
    </Modal>
  )
}
